/**
 * Tipos e conversões do cadastro do admin.
 *
 * Os tipos aqui são o espelho do que `rotas/admin.js` devolve, que é MAIS do
 * que o `api.ts` declara para `api.admin.*` (o cliente tipa só o que o painel
 * usa: nome, meta, vendido). Marca do ERP, base, `ativa` e o resumo das metas
 * existem apenas nesta tela, e é por isso que moram aqui em vez de alargar o
 * tipo compartilhado.
 *
 * Diferença que importa: `meta` e `metaGeral` voltam `null` quando nunca foram
 * cadastradas, e `null` não é zero — o campo fica vazio, não "0".
 */
import type { Fonte } from '../../api'

export type SubitemCadastro = {
  codigo: string
  rotulo: string
  ordem: number
  obs: string | null
  meta: number | null
}

export type DivisaoCadastro = {
  id: string
  nome: string
  sigla: string
  ordem: number
  base: string
  ativa: boolean
  logo: string | null
  meta: number | null
  /** Valores de `Produto Marca` que alimentam o card — mais de um por marca. */
  marcasErp: string[]
  /** Recorte por catálogo; hoje só a Private Label usa. */
  catalogosErp: string[]
  /** `id_brand` do `dw.brand` do Linx — o recorte do sell out (P-20). */
  marcasLinx: number[]
  subitens: SubitemCadastro[]
}

/**
 * `dw.brand` do Linx, lido do DW em 13/10. Mesma lista do
 * `MARCAS_LINX_CONHECIDAS` do servidor, repetida aqui para o campo mostrar o
 * nome ao lado do número — pedir "id_brand" sem de-para visível é convite a
 * digitar 2 no lugar de 3.
 *
 * Não valida nada: marca nova no Linx se cadastra pelo número e funciona.
 */
export const MARCAS_LINX: Record<number, string> = {
  1: 'LEZ A LEZ',
  2: 'LUNENDER',
  3: 'HANGAR 33',
  4: 'ALAKAZOO',
  5: 'FICO',
}

/** 'Lez a Lez (1); Hangar 33 (3)' — a dica embaixo do campo. */
export function nomesDasMarcasLinx(ids: number[]): string {
  return ids.map(id => `${MARCAS_LINX[id] ?? 'marca desconhecida'} (${id})`).join(`${SEPARADOR_ERP} `)
}

export type ResumoMetas = {
  ano: number
  metaGeral: number | null
  somaDivisoes: number
  diferenca: number | null
}

export type RespostaCadastro = {
  ano: number
  divisoes: DivisaoCadastro[]
  metaGeral: number | null
  configuracao: Record<string, string>
  resumo: ResumoMetas
  subitensDisponiveis: Array<{ codigo: string; rotulo: string }>
  bases: string[]
}

/** A aba Fontes recebe `nome` e `descricao` por fonte, que o snapshot não tem. */
export type FonteAdmin = Fonte & { nome: string; descricao: string }

/**
 * O filtro por data de faturamento, como a aba Fontes o mostra.
 *
 * As datas vêm mesmo com o filtro desligado: é o que a tela exibe como "o que
 * passa a valer se eu ligar". `null` inteiro significa cadastro fora do ar — o
 * interruptor não aparece, as fontes continuam.
 */
export type FiltroFaturamento = {
  ligado: boolean
  de: string | null
  ate: string | null
}

export type RespostaFontes = {
  fontes: FonteAdmin[]
  /** Assinaturas recusadas na ingestão. Ver `SEGURANCA.md`. */
  rejeicoes: number
  rejeicoesPorMotivo: Record<string, number>
  coletadoEm: string | null
  idadeSeg: number | null
  idadeAlertaSeg: number | null
  filtroFaturamento: FiltroFaturamento | null
}

/* -------------------------------------------------------------------------- */

/**
 * A linha em edição. Todo número é texto: campo em branco no meio da digitação
 * é estado legítimo, e guardar `number` obrigaria a inventar zero para ele —
 * zero que seria salvo como meta.
 */
export type RascunhoDivisao = {
  /** Identidade da LINHA, não da divisão: o `id` começa vazio na marca nova e
   *  muda enquanto se digita, então não serve de `key` nem de endereço. */
  chave: string
  id: string
  nome: string
  sigla: string
  ordem: string
  base: string
  ativa: boolean
  logo: string | null
  /** dataURL do `FileReader`: a prévia aparece sem ida nenhuma ao servidor. */
  previa: string | null
  /** O arquivo sobe DEPOIS do PUT das divisões — `POST /api/admin/logo/:id`
   *  responde 404 para marca que ainda não existe. */
  arquivo: File | null
  marcasErp: string
  catalogosErp: string
  /** Texto do campo: '1; 3'. Número só na hora de enviar. */
  marcasLinx: string
  subitens: string[]
  meta: string
  metasSubitem: Record<string, string>
  /** Linha acrescentada na tela e ainda não gravada. */
  nova: boolean
  /* O que veio do servidor, para o PUT poder OMITIR o que não mudou.
     `definirValoresErp` e `definirSubitens` apagam e reinserem a lista inteira,
     e a `obs` de cada valor não volta no GET — mandar a lista de novo apagaria
     a nota do catálogo da Private Label a cada "Salvar marcas". */
  marcasErpGravadas: string
  catalogosErpGravados: string
  marcasLinxGravadas: string
  subitensGravados: string[]
}

/**
 * Teto da logo, o mesmo do `TETO_LOGO_BYTES` do servidor.
 *
 * A validação que VALE é a do servidor, pelo conteúdo e não pela extensão (lá o
 * SVG com `<script>` e o ".png" que não é PNG param). Esta aqui só evita gastar
 * o upload.
 */
export const TETO_LOGO_BYTES = 512 * 1024

/** `null` = nunca cadastrada: campo vazio, nunca "0". */
export function textoDeMeta(valor: number | null | undefined): string {
  return valor === null || valor === undefined ? '' : String(valor)
}

/**
 * Separador do de-para. Ponto e vírgula, não vírgula: valor de `Produto Marca`
 * é texto livre do ERP e pode conter vírgula.
 */
export const SEPARADOR_ERP = ';'

/** Texto do campo → lista de valores do ERP, sem repetição e sem vazio. */
export function valoresDoTexto(texto: string): string[] {
  const vistos = new Set<string>()
  const saida: string[] = []
  for (const parte of texto.split(/[;\n]/)) {
    /* Sem `toUpperCase`: o valor tem de casar exatamente com o que está em
       `Produto Marca`, e trocar a caixa aqui quebraria o de-para em silêncio. */
    const valor = parte.trim()
    if (valor === '' || vistos.has(valor)) continue
    vistos.add(valor)
    saida.push(valor)
  }
  return saida
}

export function textoDosValores(valores: string[]): string {
  return valores.join(`${SEPARADOR_ERP} `)
}

/**
 * Texto do campo → lista de `id_brand`, sem repetição.
 *
 * Devolve também o que NÃO é número, para a tela nomear o problema. Descartar
 * em silêncio faria "lez" digitado no campo de marca do Linx virar lista vazia,
 * e lista vazia é um comando legítimo (desligar o sell out da divisão) — o
 * usuário salvaria acreditando ter cadastrado.
 */
export function marcasLinxDoTexto(texto: string): { ids: number[]; invalidos: string[] } {
  const vistos = new Set<number>()
  const ids: number[] = []
  const invalidos: string[] = []
  for (const parte of texto.split(/[;,\n]/)) {
    const bruto = parte.trim()
    if (bruto === '') continue
    if (!/^\d+$/.test(bruto) || Number(bruto) <= 0) {
      invalidos.push(bruto)
      continue
    }
    const id = Number(bruto)
    if (vistos.has(id)) continue
    vistos.add(id)
    ids.push(id)
  }
  return { ids, invalidos }
}

export function textoDasMarcasLinx(ids: number[]): string {
  return ids.join(`${SEPARADOR_ERP} `)
}

export function rascunhoDe(divisao: DivisaoCadastro): RascunhoDivisao {
  const metasSubitem: Record<string, string> = {}
  for (const subitem of divisao.subitens) {
    metasSubitem[subitem.codigo] = textoDeMeta(subitem.meta)
  }
  const marcasErp = textoDosValores(divisao.marcasErp)
  const catalogosErp = textoDosValores(divisao.catalogosErp)
  /* `?? []` porque painel velho e cadastro recém-migrado não mandam o campo: a
     tabela `divisao_marca_linx` nasce vazia, e `undefined.join` derrubaria a
     tela de admin inteira em vez de mostrar um campo em branco. */
  const marcasLinx = textoDasMarcasLinx(divisao.marcasLinx ?? [])
  const subitens = divisao.subitens.map(subitem => subitem.codigo)
  return {
    chave: `divisao:${divisao.id}`,
    id: divisao.id,
    nome: divisao.nome,
    sigla: divisao.sigla,
    ordem: String(divisao.ordem),
    base: divisao.base,
    ativa: divisao.ativa,
    logo: divisao.logo,
    previa: null,
    arquivo: null,
    marcasErp,
    catalogosErp,
    marcasLinx,
    subitens,
    meta: textoDeMeta(divisao.meta),
    metasSubitem,
    nova: false,
    marcasErpGravadas: marcasErp,
    catalogosErpGravados: catalogosErp,
    marcasLinxGravadas: marcasLinx,
    subitensGravados: subitens,
  }
}

export function rascunhoNovo(ordem: number, chave: string): RascunhoDivisao {
  return {
    chave, id: '', nome: '', sigla: '', ordem: String(ordem), base: 'CNF', ativa: true,
    logo: null, previa: null, arquivo: null,
    marcasErp: '', catalogosErp: '', marcasLinx: '', subitens: [],
    meta: '', metasSubitem: {}, nova: true,
    marcasErpGravadas: '', catalogosErpGravados: '', marcasLinxGravadas: '',
    subitensGravados: [],
  }
}

/** O mesmo formato que `criarDivisao` exige: minúsculas, de 2 a 16 caracteres. */
const FORMATO_ID = /^[a-z][a-z0-9_]{1,15}$/

/** Ordem conta: ela é a `ordem` que o servidor grava para cada subitem. */
function mesmaLista(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((valor, indice) => valor === b[indice])
}

export type DivisaoEnviada = {
  id: string
  nome: string
  sigla: string
  ordem: number
  base: string
  ativa: boolean
  /* AUSENTE é "não mexer nesta lista" no servidor. Lista presente é apagada e
     reinserida inteira, e o que ela não carrega (a `obs` de cada valor do ERP,
     a `obs` e o `rotulo` de cada subitem) se perde — e a `obs` nem volta no GET
     do cadastro, então a tela não teria como devolvê-la. */
  subitens?: string[]
  marcasErp?: string[]
  catalogosErp?: string[]
  marcasLinx?: number[]
}

export type EnvioDivisoes =
  | { ok: true; divisoes: DivisaoEnviada[] }
  | { ok: false; problemas: string[] }

/**
 * Monta o corpo do `PUT /api/admin/divisoes`.
 *
 * A conferência repetida aqui não substitui a do servidor — ela evita gastar um
 * PUT inteiro, que é tudo-ou-nada, por um campo em branco na décima linha.
 */
export function montarEnvioDivisoes(
  rascunhos: RascunhoDivisao[],
  bases: string[],
): EnvioDivisoes {
  const problemas: string[] = []
  const vistos = new Set<string>()
  const divisoes: DivisaoEnviada[] = []

  /* PUT vazio responde `{ok:true, gravadas:0}`, e a tela diria "Marcas salvas."
     sem ter salvado nada — justamente o que acontece quando o GET do cadastro
     falhou e a tabela ficou sem linha nenhuma. */
  if (rascunhos.length === 0) {
    return { ok: false, problemas: ['Nenhuma marca na tela para salvar; recarregue a página.'] }
  }

  for (const [indice, rascunho] of rascunhos.entries()) {
    const comoChamar = rascunho.nome.trim() || rascunho.id.trim() || `linha ${indice + 1}`
    const id = rascunho.id.trim().toLowerCase()
    if (!FORMATO_ID.test(id)) {
      problemas.push(`Id de "${comoChamar}" inválido: minúsculas sem acento, de 2 a 16 caracteres.`)
      continue
    }
    if (vistos.has(id)) {
      problemas.push(`O id "${id}" aparece em duas linhas.`)
      continue
    }
    vistos.add(id)

    const nome = rascunho.nome.trim()
    const sigla = rascunho.sigla.trim()
    if (nome === '') problemas.push(`A marca "${comoChamar}" está sem nome de painel.`)
    if (sigla === '') problemas.push(`A marca "${comoChamar}" está sem sigla.`)

    const ordem = Number(rascunho.ordem.trim())
    if (!Number.isInteger(ordem)) {
      problemas.push(`Ordem de "${comoChamar}" precisa ser um número inteiro: "${rascunho.ordem.trim()}".`)
    }
    if (!bases.includes(rascunho.base)) {
      problemas.push(`Base "${rascunho.base}" de "${comoChamar}" não existe.`)
    }

    const enviada: DivisaoEnviada = {
      id,
      nome,
      sigla,
      ordem: Number.isInteger(ordem) ? ordem : 0,
      base: rascunho.base,
      ativa: rascunho.ativa,
    }
    /* Linha nova precisa das três listas; linha gravada manda só a que mudou.
       A comparação é sobre a lista resultante, não sobre o texto: trocar
       "A;B" por "A; B" não é alteração de cadastro. */
    const subitens = [...rascunho.subitens]
    if (rascunho.nova || !mesmaLista(subitens, rascunho.subitensGravados)) {
      /* A ordem dos subitens é a posição na lista, como o servidor assume. */
      enviada.subitens = subitens
    }
    const marcas = valoresDoTexto(rascunho.marcasErp)
    if (rascunho.nova || !mesmaLista(marcas, valoresDoTexto(rascunho.marcasErpGravadas))) {
      enviada.marcasErp = marcas
    }
    const catalogos = valoresDoTexto(rascunho.catalogosErp)
    if (rascunho.nova || !mesmaLista(catalogos, valoresDoTexto(rascunho.catalogosErpGravados))) {
      enviada.catalogosErp = catalogos
    }
    const linx = marcasLinxDoTexto(rascunho.marcasLinx)
    if (linx.invalidos.length > 0) {
      problemas.push(
        `Marca do Linx de "${comoChamar}" precisa ser o número do id_brand: `
        + `"${linx.invalidos.join('", "')}" não é número.`,
      )
    }
    const linxGravadas = marcasLinxDoTexto(rascunho.marcasLinxGravadas).ids
    if (
      rascunho.nova
      || !mesmaLista(linx.ids.map(String), linxGravadas.map(String))
    ) {
      enviada.marcasLinx = linx.ids
    }
    divisoes.push(enviada)
  }

  if (problemas.length > 0) return { ok: false, problemas }
  return { ok: true, divisoes }
}
